/**
 * useAddBenchmark: the staff Add benchmark dialog's writes, in order, over
 * the staff endpoints the team-config pages use (components/evals/addBenchmark.ts),
 * and the one read the dialog makes of the chosen repository: the judge a
 * trainloop-env world names (`useWorldJudge`).
 *
 * The default model is the team's, read by whichever benchmark is scored,
 * so the model step runs only when this benchmark is the scored one after
 * the config step, or for a team with no default model at all. The judge
 * is the world's own (`world.json`), so Add writes none.
 *
 * The steps run one after another and stop at the first refusal, which
 * the dialog shows beside the step that failed. Each step is safe to
 * repeat, so submitting again after a fix carries on from where it
 * stopped: the config keeps a benchmark it already lists, the model step
 * reuses a team model with the same slug, and the inventory refuses a
 * second run while one is active.
 */

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import { getApiErrorDetail, type ApiRequestError } from '@/api/client'
import {
  createTeamConnectionApiInternalTeamsTeamIdConnectionsPost,
  createTeamModelApiInternalTeamsTeamIdModelsPost,
  getGetTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGetQueryKey,
  getGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGetQueryKey,
  getListTeamConnectionsApiInternalTeamsTeamIdConnectionsGetQueryKey,
  getListTeamJobsApiInternalTeamsTeamIdJobsGetQueryKey,
  getListTeamModelsApiInternalTeamsTeamIdModelsGetQueryKey,
  getTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGet,
  listTeamModelsApiInternalTeamsTeamIdModelsGet,
  runTeamJobApiInternalTeamsTeamIdJobsJobSlugRunPost,
  useGetTeamWorldJudgeApiInternalTeamsTeamIdWorldJudgeGet,
  useListPlatformGithubAppInstallationsApiInternalGithubAppInstallationsGet,
  setTeamDefaultModelApiInternalTeamsTeamIdModelsModelIdDefaultPost,
  updateTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigPut,
} from '@/api/generated/internal/internal'
import { getGetOverviewApiEvalGroupsOverviewGetQueryKey } from '@/api/generated/evals/evals'
import { getListModelsApiModelsGetQueryKey } from '@/api/generated/models/models'
import type {
  BenchmarkAdapterItem,
  ModelResponse,
  PlatformGithubAppInstallation,
  ProxyModelListItem,
} from '@/api/generated/model'
import {
  INVENTORY_JOB,
  appearedInstallation,
  configChange,
  modelConflict,
  setsDefaultModel,
  type AddBenchmarkInput,
  type WorldJudge,
} from '@/components/evals/addBenchmark'
import { GITHUB_CLOUD_API_BASE_URL } from '@/components/internal/team-config/githubConnection'
import { useDebounce } from '@/hooks/useDebounce'

/** The dialog's steps, in the order they run. */
export type AddStep = 'config' | 'model' | 'inventory'

export const ADD_STEPS: readonly AddStep[] = ['config', 'model', 'inventory']

export interface AddBenchmarkResult {
  /** Whether the benchmark is now the one the night scores. */
  scored: boolean
  /** The benchmark inventory Job the last step started. */
  jobName: string
}

interface AddBenchmarkVariables {
  input: AddBenchmarkInput
  adapters: readonly BenchmarkAdapterItem[]
  catalog: readonly ProxyModelListItem[]
}

/** A refusal the form makes itself, before the api is asked. */
class AddBenchmarkError extends Error {}

/**
 * What the config step decided. `scored` is this benchmark being the one
 * the night scores (`becomesScored`), so the default model is set for it
 * (`setsModel`, also true for a team with none). Decided from the
 * benchmark, not from whether the row had a layout, so a second Add after
 * a refused step still sets it.
 */
interface ConfigSaved {
  scored: boolean
  setsModel: boolean
}

async function saveConfig(
  teamId: string,
  input: AddBenchmarkInput,
  adapters: readonly BenchmarkAdapterItem[],
): Promise<ConfigSaved> {
  const read = await getTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGet(teamId)
  if (read.status !== 200) throw new AddBenchmarkError('The team config could not be read.')
  const change = configChange(read.data, input, adapters)
  if (change.kind === 'refused') throw new AddBenchmarkError(change.reason)
  if (change.kind === 'save') {
    await updateTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigPut(teamId, {
      ...change.body,
      expected_updated_at: read.data.updated_at,
    })
  }
  return { scored: change.scored, setsModel: setsDefaultModel(read.data, change.scored) }
}

/** The team model with the input's slug, created on the catalog model when the team has none. */
async function teamModel(
  teamId: string,
  input: AddBenchmarkInput,
  catalog: readonly ProxyModelListItem[],
): Promise<ModelResponse> {
  const listed = await listTeamModelsApiInternalTeamsTeamIdModelsGet(teamId)
  if (listed.status !== 200) throw new AddBenchmarkError('The team models could not be read.')
  const existing = listed.data.models.find((row) => row.model.slug === input.modelSlug)
  if (existing !== undefined) {
    const conflict = modelConflict(existing, input.baseModel)
    if (conflict !== null) throw new AddBenchmarkError(conflict)
    return existing.model
  }
  if (input.baseModel === '')
    throw new AddBenchmarkError('Pick the base model the new team model runs on.')
  const base = catalog.find((item) => item.slug === input.baseModel)
  if (base === undefined)
    throw new AddBenchmarkError(`${input.baseModel} is not in the model catalog.`)
  const created = await createTeamModelApiInternalTeamsTeamIdModelsPost(teamId, {
    proxy_model_id: base.id,
    slug: input.modelSlug,
    display_name: input.modelDisplay.trim() === '' ? base.display_name : input.modelDisplay.trim(),
  })
  if (created.status !== 201) throw new AddBenchmarkError('The model was not created.')
  return created.data
}

async function setDefaultModel(
  teamId: string,
  input: AddBenchmarkInput,
  catalog: readonly ProxyModelListItem[],
): Promise<void> {
  const model = await teamModel(teamId, input, catalog)
  if (!model.is_default) {
    await setTeamDefaultModelApiInternalTeamsTeamIdModelsModelIdDefaultPost(teamId, model.id)
  }
}

async function startInventory(teamId: string): Promise<string> {
  const started = await runTeamJobApiInternalTeamsTeamIdJobsJobSlugRunPost(teamId, INVENTORY_JOB)
  if (started.status !== 200) throw new AddBenchmarkError('The benchmark inventory did not start.')
  return started.data.job_name
}

/** Every read the steps change, refetched once the dialog is done. */
function invalidateTeamReads(queryClient: ReturnType<typeof useQueryClient>, teamId: string): void {
  for (const queryKey of [
    getGetOverviewApiEvalGroupsOverviewGetQueryKey(),
    getGetTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGetQueryKey(teamId),
    getGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGetQueryKey(teamId),
    getListTeamModelsApiInternalTeamsTeamIdModelsGetQueryKey(teamId),
    getListTeamJobsApiInternalTeamsTeamIdJobsGetQueryKey(teamId),
    getListModelsApiModelsGetQueryKey(),
  ]) {
    void queryClient.invalidateQueries({ queryKey })
  }
}

/**
 * The Add button's run. `onStep` names the step about to run, so the
 * dialog can mark the one a refusal belongs to.
 */
export function useAddBenchmark(
  teamId: string,
  onStep: (step: AddStep) => void,
): UseMutationResult<
  AddBenchmarkResult,
  ApiRequestError | AddBenchmarkError,
  AddBenchmarkVariables
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ input, adapters, catalog }: AddBenchmarkVariables) => {
      onStep('config')
      const { scored, setsModel } = await saveConfig(teamId, input, adapters)
      onStep('model')
      if (setsModel) await setDefaultModel(teamId, input, catalog)
      onStep('inventory')
      const jobName = await startInventory(teamId)
      return { scored, jobName }
    },
    onSettled: () => {
      invalidateTeamReads(queryClient, teamId)
    },
  })
}

/**
 * How long the branch must stay unchanged before the judge is read. Each
 * read makes three GitHub calls under the team's installation, so typing
 * a branch name reads once, after the last keystroke.
 */
const JUDGE_READ_DELAY_MS = 500

/**
 * The judge the chosen trainloop-env world names in its `world.json`, read
 * through the team's installation by the api. Read afresh whenever the dialog asks again
 * or the tab regains focus, so a world.json fixed on GitHub meanwhile shows
 * without reopening the dialog.
 */
export function useWorldJudge(teamId: string, input: AddBenchmarkInput): WorldJudge {
  const repo = input.repo.trim()
  const ref = input.ref.trim()
  const settled = useDebounce(ref, JUDGE_READ_DELAY_MS)
  const picked = repo !== '' && ref !== ''
  const query = useGetTeamWorldJudgeApiInternalTeamsTeamIdWorldJudgeGet(
    teamId,
    { repo, ref },
    {
      query: {
        enabled: picked && settled === ref,
        staleTime: 0,
        refetchOnWindowFocus: true,
      },
    },
  )
  if (!picked) return { kind: 'unpicked' }
  if (settled !== ref) return { kind: 'reading' }
  if (query.isError) {
    return {
      kind: 'unreadable',
      detail: getApiErrorDetail(query.error) ?? 'The world.json of this branch could not be read.',
    }
  }
  if (query.data?.status !== 200) return { kind: 'reading' }
  const { model } = query.data.data
  return model === null ? { kind: 'unnamed' } : { kind: 'named', model }
}

/** Link the team to a GitHub App installation, so its repositories can be listed. */
export function useConnectGithub(
  teamId: string,
): UseMutationResult<unknown, ApiRequestError, { installationId: number }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ installationId }: { installationId: number }) =>
      createTeamConnectionApiInternalTeamsTeamIdConnectionsPost(teamId, {
        kind: 'github',
        display_name: 'GitHub',
        installation_id: installationId,
        api_base_url: GITHUB_CLOUD_API_BASE_URL,
      }),
    onSuccess: () => {
      for (const queryKey of [
        getGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGetQueryKey(teamId),
        getListTeamConnectionsApiInternalTeamsTeamIdConnectionsGetQueryKey(teamId),
      ]) {
        void queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}

/**
 * How often the dialog lists the App's installations after Install opens
 * GitHub, and for how long. Each listing signs an App JWT, and more than
 * SIGNATURE_SURGE_LIMIT (100) in ten minutes pages #platform
 * (services/github_app.py), so the poll is slow and stops on its own.
 * Coming back to the tab lists them again at once.
 */
const INSTALL_POLL_MS = 15_000
const INSTALL_POLL_LIMIT_MS = 10 * 60_000

export interface GithubInstall {
  /** The App's installations, newest first; undefined until listed. */
  installations: readonly PlatformGithubAppInstallation[] | undefined
  /** Why the installations could not be listed. */
  listError: ApiRequestError | null
  /** Whether the installations are being listed right now. */
  listing: boolean
  /** List the installations again, after a failed listing. */
  retry: () => void
  /**
   * Call as Install opens GitHub. It lists the installations afresh, never
   * from the cache, since an installation made while the cache was stale
   * would otherwise count as this team's new one.
   */
  start: () => Promise<void>
  /** Whether Install was pressed and nothing was linked yet. */
  waiting: boolean
  /** The link of one installation to the team, on its own or from a Link button. */
  connect: UseMutationResult<unknown, ApiRequestError, { installationId: number }>
}

/**
 * The dialog's install flow (TRA-1577). Install opens the App's install
 * page on GitHub. The dialog then lists the App's installations again
 * whenever the tab regains focus, and every INSTALL_POLL_MS for a while,
 * and links on its own the one installation that is new since Install
 * (`appearedInstallation`). An organization that already has the App
 * creates no new installation, so staff link it from the list.
 */
export function useGithubInstall(teamId: string): GithubInstall {
  const connect = useConnectGithub(teamId)
  const [started, setStarted] = useState<{ at: number; before: readonly number[] } | null>(null)
  const listing = useListPlatformGithubAppInstallationsApiInternalGithubAppInstallationsGet({
    query: {
      // Listed afresh every time the dialog opens: a cached list (the app's
      // staleTime is five minutes) would hide an installation made since.
      staleTime: 0,
      refetchOnWindowFocus: started === null ? false : 'always',
      refetchInterval: () =>
        started !== null && Date.now() - started.at < INSTALL_POLL_LIMIT_MS
          ? INSTALL_POLL_MS
          : false,
    },
  })
  const installations = listing.data?.status === 200 ? listing.data.data.installations : undefined
  const appeared = appearedInstallation(started?.before ?? null, installations)?.installation_id
  // The installation this dialog already tried to link on its own: a refused
  // link (one that misses a benchmark repository) is not retried every poll.
  const tried = useRef<number | null>(null)
  const { mutate } = connect
  useEffect(() => {
    if (appeared === undefined || tried.current === appeared) return
    tried.current = appeared
    mutate({ installationId: appeared })
  }, [appeared, mutate])
  return {
    installations,
    listError: listing.error,
    listing: listing.isFetching,
    retry: () => {
      void listing.refetch()
    },
    start: async () => {
      const fresh = await listing.refetch()
      if (fresh.data?.status !== 200) return
      const before = fresh.data.data.installations.map((i) => i.installation_id)
      setStarted({ at: Date.now(), before })
    },
    waiting: started !== null && !connect.isSuccess,
    connect,
  }
}
